import { Component, Input, OnChanges } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { fillSample } from './variables';

const PREVIEW_KEY = 'tdt-show-email-preview';

/** Whether the side preview is shown (remembered per browser; default on). */
export function previewShown(): boolean {
  try { return localStorage.getItem(PREVIEW_KEY) !== '0'; } catch { return true; }
}
export function setPreviewShown(v: boolean): void {
  try { localStorage.setItem(PREVIEW_KEY, v ? '1' : '0'); } catch { /* storage blocked: just not remembered */ }
}

/**
 * Email preview with sample data. Rendered in a sandboxed iframe (no scripts, no same-origin),
 * so template markup can't run code in the app, and full documents show their own <head> styles.
 */
@Component({
  selector: 'app-email-preview',
  standalone: true,
  template: `
    <div class="ep">
      <div class="ep-head">
        @if (from) { <span>From</span><div>{{ from }}</div> }
        <span>To</span><div>{{ to }}</div>
        <span>Subject</span><div class="ep-subject">{{ subjectText || 'No subject yet' }}</div>
      </div>
      <iframe class="ep-body" title="Email preview" sandbox="" [srcdoc]="doc"></iframe>
    </div>`,
  styles: [`
    .ep { border: 1px solid #E2E8F0; border-radius: 10px; background: #fff; overflow: hidden; }
    .ep-head { display: grid; grid-template-columns: auto minmax(0, 1fr); gap: 4px 12px; padding: 12px 16px; border-bottom: 1px solid #E2E8F0; font-size: 13px; }
    .ep-head span { color: #64748B; }
    .ep-subject { font-weight: 500; font-size: 15px; }
    .ep-body { display: block; width: 100%; height: 460px; border: 0; }
  `],
})
export class EmailPreviewComponent implements OnChanges {
  @Input() subject = '';
  @Input() body = '';
  @Input() isHtml = true;
  @Input() from = '';
  @Input() to = 'owner@example.com';
  @Input() cidUrls: Record<string, string> = {};
  subjectText = '';
  doc: SafeHtml = '';

  constructor(private sanitizer: DomSanitizer) {}

  ngOnChanges(): void {
    this.subjectText = fillSample(this.subject);
    let html = fillSample(this.body);
    if (!this.isHtml) {
      const esc = html.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      html = `<pre style="font-family:inherit;white-space:pre-wrap;margin:0">${esc}</pre>`;
    }
    html = html.replace(/src="cid:([^"]+)"/g, (m, cid) => this.cidUrls[cid] ? `src="${this.cidUrls[cid]}"` : m);
    const base = '<style>body{font:14px/1.6 Arial,Helvetica,sans-serif;color:#222;margin:16px}img{max-width:100%}</style>';
    // Safe to trust: the iframe is sandboxed without allow-scripts / allow-same-origin.
    this.doc = this.sanitizer.bypassSecurityTrustHtml(/<head[\s>]/i.test(html) ? html.replace(/<head([^>]*)>/i, `<head$1>${base}`) : base + html);
  }
}
